import { Copy, UserPlus } from 'lucide-react';
import { customerFlowNames } from 'src/components/CustomerId';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';

import { getResultColor, getStatusColor } from './sessionColors';
import { Column } from './TableViewModal';

const getReviewedDate = (session: any) => {
  const candidate =
    session?.reviewedDate ||
    session?.updatedDate ||
    session?.completedDate ||
    session?.flowStatus?.updatedAt ||
    session?.flowStatus?.completedAt ||
    null;
  if (!candidate) return null;
  try {
    const d = new Date(candidate);
    return {
      date: d.toLocaleDateString('en-GB'),
      time: d.toLocaleTimeString('en-GB'),
    };
  } catch {
    return null;
  }
};

const getInitials = (name: string) => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

interface SessionsTableRowProps {
  session: any;
  selectedColumns: Column[];
  gridTemplateColumns: string;
  copiedId: string | null;
  onCopyToClipboard: (text: string, id: string) => Promise<void>;
  onViewClick: (id: string) => void;
  onAssignClick: (session: any) => void;
  flowNameLookup?: Record<string, string>;
}

const SessionsTableRow = ({
  session,
  selectedColumns,
  gridTemplateColumns,
  copiedId,
  onCopyToClipboard,
  onViewClick,
  onAssignClick,
  flowNameLookup,
}: SessionsTableRowProps) => (
  <div
    key={session.sessionId}
    className="bg-white border-b border-[#EEF1F5] last:border-b-0 transition-colors duration-150 cursor-pointer hover:bg-[#F8FAFD] text-sm text-[#252525]"
    style={{ height: '56px' }}
    onClick={() => onViewClick(session.sessionId)}
  >
    <div
      className="grid items-center h-full px-2 text-center"
      style={{ gap: 10, gridTemplateColumns }}
    >
      {selectedColumns.includes('sonioId') && (
        <div
          className="font-mono text-[12.5px] text-[#6B6B6B] relative flex items-center justify-center gap-1"
          title={session.sessionId}
        >
          <div className="flex items-center gap-1 group min-w-0 max-w-full">
            <span
              className="block truncate cursor-pointer min-w-0"
              onClick={e => {
                e.stopPropagation();
                onCopyToClipboard(
                  session.sessionId,
                  `sonioId-${session.sessionId}`,
                );
              }}
            >
              {session.sessionId}
            </span>
            <Copy
              className="h-3 w-3 opacity-0 group-hover:opacity-100 flex-shrink-0 cursor-pointer"
              onClick={e => {
                e.stopPropagation();
                onCopyToClipboard(
                  session.sessionId,
                  `sonioId-${session.sessionId}`,
                );
              }}
            />
          </div>
          {copiedId === `sonioId-${session.sessionId}` && (
            <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-[#0C2340] text-white text-xs px-2 py-1 rounded-md whitespace-nowrap">
              Copied!
            </span>
          )}
        </div>
      )}

      {selectedColumns.includes('referenceId') && (
        <div
          className="relative flex items-center justify-center gap-1"
          title={session.referenceId}
        >
          <div className="flex items-center gap-1 group min-w-0 max-w-full">
            <span
              className="block truncate cursor-pointer min-w-0"
              onClick={e => {
                e.stopPropagation();
                if (session.referenceId) {
                  onCopyToClipboard(
                    session.referenceId,
                    `referenceId-${session.sessionId}`,
                  );
                }
              }}
            >
              {session.referenceId || ''}
            </span>
            {session.referenceId && (
              <Copy
                className="h-3 w-3 opacity-0 group-hover:opacity-100 flex-shrink-0 cursor-pointer"
                onClick={e => {
                  e.stopPropagation();
                  onCopyToClipboard(
                    session.referenceId,
                    `referenceId-${session.sessionId}`,
                  );
                }}
              />
            )}
          </div>
          {copiedId === `referenceId-${session.sessionId}` && (
            <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-[#0C2340] text-white text-xs px-2 py-1 rounded-md whitespace-nowrap">
              Copied!
            </span>
          )}
        </div>
      )}

      {selectedColumns.includes('flowName') && (
        <div
          className="relative flex items-center justify-center gap-1"
          title={session.flowId}
        >
          <div className="flex items-center gap-1 group min-w-0 max-w-full">
            <span
              className="block truncate cursor-pointer min-w-0"
              onClick={e => {
                e.stopPropagation();
                if (session.flowId) {
                  onCopyToClipboard(
                    session.flowId,
                    `flowId-${session.sessionId}`,
                  );
                }
              }}
            >
              {flowNameLookup?.[session.flowId] ??
                session.flowName ??
                (customerFlowNames as Record<string, string>)[session.flowId] ??
                session.flowId ??
                ''}
            </span>
            {session.flowId && (
              <Copy
                className="h-3 w-3 opacity-0 group-hover:opacity-100 flex-shrink-0 cursor-pointer"
                onClick={e => {
                  e.stopPropagation();
                  onCopyToClipboard(
                    session.flowId,
                    `flowId-${session.sessionId}`,
                  );
                }}
              />
            )}
          </div>
          {copiedId === `flowId-${session.sessionId}` && (
            <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-[#0C2340] text-white text-xs px-2 py-1 rounded-md whitespace-nowrap">
              Copied!
            </span>
          )}
        </div>
      )}

      {selectedColumns.includes('fullName') && (
        <div className="text-[#252525]">
          <span className="block truncate" title={session.name || ''}>
            {session.name || ''}
          </span>
        </div>
      )}

      {selectedColumns.includes('firstName') && (
        <div className="text-[#252525]">
          <span className="truncate block" title={session.firstName || ''}>
            {session.firstName || ''}
          </span>
        </div>
      )}

      {selectedColumns.includes('lastName') && (
        <div className="text-[#252525]">
          <span className="truncate block" title={session.lastName || ''}>
            {session.lastName || ''}
          </span>
        </div>
      )}

      {selectedColumns.includes('dateReviewed') && (
        <div className="text-[#252525]">
          {(() => {
            const reviewed = getReviewedDate(session);
            return reviewed ? (
              <div className="flex flex-col">
                <span className="font-medium">{reviewed.date}</span>
                <span className="text-xs text-[#5B6B80]">{reviewed.time}</span>
              </div>
            ) : (
              <span className="text-[#5B6B80]">—</span>
            );
          })()}
        </div>
      )}

      {selectedColumns.includes('dateCreated') && (
        <div className="text-[#252525]">
          <div className="flex flex-col">
            <span className="font-medium">
              {new Date(session.createdDate).toLocaleDateString('en-GB')}
            </span>
            <span className="text-xs text-[#5B6B80]">
              {new Date(session.createdDate).toLocaleTimeString('en-GB')}
            </span>
          </div>
        </div>
      )}

      {selectedColumns.includes('status') && (
        <div className="flex justify-center">
          <Badge className={getStatusColor(session.status)}>
            {session.status}
          </Badge>
        </div>
      )}

      {selectedColumns.includes('result') && (
        <div className="flex justify-center">
          <Badge className={getResultColor(session.validation)}>
            {session.validation?.replace(/_/g, ' ')}
          </Badge>
        </div>
      )}

      {selectedColumns.includes('assign') && (
        <div className="flex justify-center gap-1">
          {session.assignedUser ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="h-8 w-8 rounded-full bg-[#00387D] text-white flex items-center justify-center text-xs font-semibold cursor-pointer hover:bg-[#00387D]/90 transition-colors"
                    onClick={e => {
                      e.stopPropagation();
                      onAssignClick(session);
                    }}
                  >
                    {getInitials(session.assignedUser.name)}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{session.assignedUser.name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : session.validation === 'NEEDS_REVIEW' ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={e => {
                      e.stopPropagation();
                      onAssignClick(session);
                    }}
                    className="h-8 w-8 p-0"
                  >
                    <UserPlus className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Assign Session</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
        </div>
      )}
    </div>
  </div>
);

export default SessionsTableRow;
